import { DevError, type IPool, call, createErrorObj, dev, invariant, str } from "@phosphor/utils";
import { DisposePool } from "@phosphor/utils";
import { type Atom, atom, useAtomValue } from "jotai";
import type { ILogger } from "librarylog";
import { JotaiPubSub } from "../lib/jotai/JotaiPubSub.ts";
import type { JotaiStore } from "../lib/jotai/jotai-types.ts";
import { watchAtom } from "../lib/jotai/watchAtom.ts";
import { MapOrSetDefault } from "../lib/js/MapOrSetDefault.ts";
import type { UID, World, WorldState } from "./WorldState.ts";
import { invariantHas } from "./invariantHas.ts";

const identity = <T>(x: T) => x;

/**
 * Easy data structure to create a quick map from a UID to some replayed last event.
 *
 * `T extends object` is used here to prevent `null | ...` and `undefined | ...` unions because
 * they are hard to distinguish the difference between no value and values that are
 * null or undefined.
 */
class UIDPubSub<T extends object> extends JotaiPubSub<UID, T> {}

const worldStateAtom = atom<{ world: WorldState; dispose(): void } | undefined>(undefined);
const NO_MATCH = Symbol.for("World/plugin-hook/NoMatch");

export function useWorldStateViaJotaiStore(): WorldState {
  const worldState = useAtomValue(worldStateAtom);
  invariant(worldState, "WorldState not found");
  return worldState.world;
}

export function useWorldStateMaybe(): WorldState | undefined {
  return useAtomValue(worldStateAtom)?.world;
}

const RE_VALID_IDENTIFIER = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;
const macros = {
  access: (id: string, name: string) =>
    RE_VALID_IDENTIFIER.test(name) ? `${id}.${name}` : `${id}[${JSON.stringify(name)}]`,
  objPropAssign: (to: string, name: string) =>
    RE_VALID_IDENTIFIER.test(name) ? `${name}: ${to}` : `[${JSON.stringify(name)}]: ${to}`,
};

type OnCreationHookFn = { plugin: World.WorldStatePlugin; hookNo: number } & ((
  uid: UID,
  components: World.EntityCreatedCallbackComponents<World.AnyEntityCreatedFnOptions>,
  world: WorldState,
  onDeath: IPool,
) => object | void | typeof NO_MATCH);

/**
 * @example
 *
 * // Create a new entities map with a Jotai store and disposable stack
 * const store = createStore();
 * const pool = new DisposePool();
 * const world = new WorldState({ store, pool, plugins: [] });
 *
 * // Add an entity with a description component
 * const uid: UID = "node-123" as UID;
 * world.addEntity(uid, VariableEntity, {
 *   description: {
 *     textEditor: {}
 *   },
 *   // ...
 * });
 *
 * // Get the description component atom for the entity
 * const descriptionAtom: Atom<{ textEditor: TextEditor } | undefined> =
 *   world.getComponent(uid, CDescription);
 *
 * // Subscribe to changes
 * store.sub(descriptionAtom, () => {
 *   const description = store.get(descriptionAtom);
 *   log._debug("Description updated", { description });
 * });
 *
 * // When done, dispose of the stack to clean up atoms
 * pool.dispose();
 */
export class WorldStateImpl implements WorldState {
  static MAX_ENTITY_CREATION_LOOPS = 4;
  #onlyUnique = call(() => {
    const map = new Map<string, { thing: object; seenFrom: string }>();
    return (things: { key: string } | Array<{ key: string }>, seenFrom: string) => {
      for (const thing of Array.isArray(things) ? things : [things]) {
        const existing = map.get(thing.key);
        if (!existing) {
          map.set(thing.key, { thing, seenFrom });
          continue;
        }
        if (existing.thing !== thing) {
          throw new Error(
            `Duplicate item key "${thing.key}" first seen from "${existing.seenFrom}" now seen from "${seenFrom}"`,
          );
        }
      }
    };
  });
  #uniques: JotaiPubSub<string, object>;
  #uniqueAdditions = new Map<string, World.WorldStatePlugin>(); // Maps unique name to plugin name
  #entities = new MapOrSetDefault<World.EntityBundle & { _onDeath: DisposePool }, UID>((id) => ({
    _id: id,
    _name: "<unknown>",
    _source: dev`<unknown>`.ctx({ loc: "phosphor-blog/scripts/vmc/WorldState-impl.ts:105:14" }),
    _onDeath: new DisposePool(),
  }));
  #entityLifetimePools = new MapOrSetDefault<DisposePool, UID>((id) => this.pool.add(new DisposePool()));
  // Future: Allow to destroy/replace individual plugins
  #pluginLifetimePools = new MapOrSetDefault<DisposePool, string>((name) => this.pool.add(new DisposePool()));
  #componentValues = new MapOrSetDefault<UIDPubSub<any>>((name) => new UIDPubSub(this.store, name, this.gcPool));
  readonly #hooks: {
    onCreatedPlugins: World.WorldStatePlugin[];
    onCreated: OnCreationHookFn[];
  } = {
    onCreatedPlugins: [],
    onCreated: [],
  };

  public readonly store: JotaiStore;
  public readonly pool: DisposePool;
  public readonly gcPool: DisposePool;

  get _dev() {
    return {
      _name: this.constructor.name,
      entities: this.#entities,
      componentValues: this.#componentValues,
      uniques: this.#uniques["map"],
      hooks: this.#hooks,
      hooksInspectable: this.#hooks.onCreated.map((hook, i) => {
        return { name: `${this.#hooks.onCreatedPlugins[i]!.name.toMessageString()}`, hook };
      }),
    };
  }

  constructor(
    private log: ILogger,
    options: { store: JotaiStore; pool: DisposePool; plugins?: World.WorldStatePlugin[]; strict?: boolean },
  ) {
    const { store, pool: parentPool, plugins = [] } = options;
    const pool = (this.pool = parentPool.child());
    this.gcPool = new DisposePool();
    if (options.strict) {
      this.pool.add(() => {
        if (typeof requestIdleCallback === "function") {
          void requestIdleCallback(() => this.gcPool.dispose());
        } else {
          this.gcPool.dispose();
        }
      });
    }
    this.log = log.named("WorldState");
    this.store = store;

    // Add cleanup to disposable stack
    pool.add(() => {
      // console.info("Disposing WorldState");
      // Clear entity map atom
      store.set(worldStateAtom, (current) => {
        return current?.world === this ? undefined : current;
      });
    });

    store.set(worldStateAtom, (prev) => {
      if (prev) prev.dispose();
      if (options.strict) {
        invariant(!prev, "WorldState already exists. Only one WorldState is allowed per store to prevent atom issues.");
      }
      return { world: this, dispose: () => pool.dispose() };
    });

    const uniques = (this.#uniques = new JotaiPubSub(this.store, "uniques", this.gcPool));
    const seenPlugins = new WeakSet<World.WorldStatePlugin>();
    if (plugins.length > 0) {
      const worldAtom = atom<WorldState | undefined>(undefined);
      const onErr = (plugin: World.WorldStatePlugin, doing: string, err: unknown) => {
        console.error(`Error in ${plugin.name.toMessageString()} while ${doing}`, err);
      };
      const hooks = this.#hooks;
      const getUniqueAtom = (seenFrom: string, unique: World.AnyUniqueClass) => {
        this.#onlyUnique(unique, seenFrom);
        return uniques.getAtom(unique.key);
      };
      const getUniqueOrThrow = (seenFrom: string, unique: World.AnyUniqueClass) => {
        this.#onlyUnique(unique, seenFrom);
        return this.getUniqueOrThrow(unique);
      };
      // reverse, since we want to add plugins in order and we pop them off the stack
      const pluginStack = plugins.reverse();
      const addPlugin = (plugin: World.WorldStatePlugin) => {
        if (seenPlugins.has(plugin)) {
          this.log._debug(`Plugin ${plugin.name.toMessageString()} already added`);
          return;
        }
        seenPlugins.add(plugin);
        pluginStack.push(plugin);
      };
      let nextPlugin: World.WorldStatePlugin | undefined;
      while ((nextPlugin = pluginStack.pop()) != null) {
        const plugin = nextPlugin;
        const pluginName = plugin.name.toMessageString();
        const onPluginDispose = this.#pluginLifetimePools.getOrSetDefault(pluginName);
        const pluginAddUnique = (unique: World.AnyUniqueClass, value: World.AnyUniqueClass["Type"]) => {
          this.#onlyUnique(unique, `pluginAddUnique call`);
          const existingValue = store.get(uniques.getAtom(unique.key));
          if (existingValue !== undefined) {
            const addedBy = this.#uniqueAdditions.get(unique.key);
            throw new Error(
              `Failed to add unique (${unique.key}) for plugin (${pluginName}) because it was already added by "${addedBy?.name?.toMessageString()}"`,
            );
          }
          this.#uniqueAdditions.set(unique.key, plugin);
          uniques.emit(unique.key, Object.assign(value, { _source: plugin.name }));
        };
        let pluginHookN = 0;
        plugin.setup({
          onDestroy: onPluginDispose,
          log: this.log,
          store,
          worldAtom,
          addPlugin,
          getUniqueAtom: getUniqueAtom.bind(this, `getUniqueAtom call from plugin (${pluginName})`),
          getUniqueOrThrow: getUniqueOrThrow.bind(this, `getUniqueOrThrow call from plugin (${pluginName})`),
          addUnique: pluginAddUnique,
          getComponentAtom: this.getComponentAtom.bind(this),
          onEntityCreated: (options, callback) => {
            this.#onlyUnique(options.requires, `onEntityCreated call [requires] for plugin (${pluginName})`);
            this.#onlyUnique(options.optional ?? [], `onEntityCreated call [optional] for plugin (${pluginName})`);
            this.#onlyUnique(options.provides ?? [], `onEntityCreated call [provides] for plugin (${pluginName})`);
            const condHasAllRequirements = options.requires
              .map((c) => `${macros.access("entityBundle", c.key)} != null`)
              .join("&&");
            // e.g. `a: a0, b: a1, [" c"]: a2`
            const inner = call(() => {
              const callbackCall = `callback(uid, entityBundle, world, onDeath, define)`;
              if (options.provides) {
                const props = options.provides.map((c, i) => macros.objPropAssign(`P${i}`, c.key)).join(", ");
                return `const {${props}} = ${callbackCall} ?? {}; return {${props}};`;
              } else {
                // no return
                return callbackCall;
              }
            });
            const body = str`
              try { if (${condHasAllRequirements}) { ${inner} } else return NO_MATCH;
              } catch (err) { onErr(err) }`;
            hooks.onCreatedPlugins.push(plugin);
            hooks.onCreated.push(
              Object.assign(
                // eslint-disable-next-line @typescript-eslint/no-implied-eval
                new Function(
                  "callback",
                  "onErr",
                  "define",
                  "NO_MATCH",
                  "uid",
                  "entityBundle",
                  "world",
                  "onDeath",
                  body,
                ).bind(this, callback, onErr.bind(null, plugin, "entity creation hook"), identity, NO_MATCH),
                { plugin, hookNo: ++pluginHookN },
              ),
            );
          },
          watch: (atom, fn, eq = Object.is) => {
            pool.add(watchAtom(this.store, atom, fn, eq));
          },
        });
      }
      // after all plugins are done, we can set the world
      store.set(worldAtom, this);
    }
    // Add cleanup to disposable stack
    this.gcPool.add(() => {
      this.#entities.clear();
      this.#componentValues.clear();
      this.#hooks.onCreated.length = 0;
      this.#hooks.onCreatedPlugins.length = 0;
      this.#uniqueAdditions.clear();
      this.#componentValues = createErrorObj("WorldState disposed");
      this.#entities = createErrorObj("WorldState disposed");
      this.#uniques = createErrorObj("WorldState disposed");
    });
  }

  /** Pass a second argument to assert that the entity has the components */
  public getEntity(id: UID<World.AnyTarget>, components?: World.AnyComponents): World.EntityBundle {
    components && this.#onlyUnique(components, `getEntity (${id}) components call`);
    const found = this.#entities.get(id);
    invariant(found, "Entity not found", id);
    for (const component of components ?? []) {
      invariantHas(found, component, `Entity ${id} does not have component "${component.key}"`);
    }
    return found;
  }
  public getEntityMaybe(id: UID<World.AnyTarget>, components?: World.AnyComponents): World.EntityBundle | undefined {
    const entity = this.#entities.get(id);
    if (!entity) return undefined;

    // If components are specified, verify they all exist
    if (components) {
      for (const component of components) {
        if (this.getComponentMaybe(id, component) === undefined) {
          return undefined;
        }
      }
    }

    return entity;
  }

  public addEntity<T extends World.AnyEntityClass>(
    id: UID<undefined>,
    entityClass: T,
    components: World.EntityCreationValues<T>,
  ): UID<T> {
    this.#onlyUnique(entityClass, `addEntity(${id}, ${entityClass.key})`);
    this.#onlyUnique(entityClass.components, `addEntity(${id}, ${entityClass.key}) components call`);
    if (this.#entities.has(id)) {
      throw new DevError(`Entity (${entityClass.key}) already exists with id ${id}`);
    }
    // Debugging
    // console.log(`Adding entity "${entityClass.name}" (${id})`);
    // if (this.world.has(id)) {
    //   console.log(`Entity already exists ${entityClass.name} with id ${id}`);
    // }
    const onEntityDeath = this.#entityLifetimePools.getOrSetDefault(id);
    let pendingHooks = [...this.#hooks.onCreated];
    const appliedHooks: { hook: OnCreationHookFn; provided: string[] }[] = [];
    const formatHook = (hook: OnCreationHookFn) => `${hook.plugin.name.toMessageString()} (hook: #${hook.hookNo})`;
    const formatAppliedHooksStr = () =>
      appliedHooks
        .map(
          ({ hook, provided }) => `${formatHook(hook)} provided: ${provided.length > 0 ? provided.join(", ") : "none"}`,
        )
        .join("; ");
    let iterations = 0;

    while (pendingHooks.length > 0 && iterations < WorldStateImpl.MAX_ENTITY_CREATION_LOOPS) {
      let changed = false;
      const remainingHooks: typeof pendingHooks = [];
      iterations++;

      for (const hook of pendingHooks) {
        if (appliedHooks.some((h) => h.hook === hook)) continue;
        const newComponents = hook(id, components, this, onEntityDeath);
        if (newComponents === NO_MATCH) {
          remainingHooks.push(hook);
          continue;
        }
        if (newComponents) {
          for (const key in newComponents) {
            if (components[key] != null) {
              throw new DevError(
                `Entity (${entityClass.key}) already has component "${key}" (attempted to add from hook ${formatHook(hook)}) (previous hooks: ${formatAppliedHooksStr()})`,
                { components, newComponents },
              );
            }
          }
          Object.assign(components, newComponents);
          changed = true;
        }
        appliedHooks.push({ hook, provided: Object.keys(newComponents ?? {}) });
      }
      pendingHooks = remainingHooks;
      if (!changed) break;
    }

    if (iterations >= WorldStateImpl.MAX_ENTITY_CREATION_LOOPS) {
      this.log._hmm(
        `Warning: Some entity creation hooks could not be resolved within ${WorldStateImpl.MAX_ENTITY_CREATION_LOOPS} iterations for entity (${entityClass.key}) with id ${id} (last applied hooks: ${formatAppliedHooksStr()})`,
      );
    }
    Object.assign(
      this.#entities.getOrSetDefault(id as UID),
      {
        _name: entityClass.key,
        _hooks: appliedHooks.map(({ hook: { plugin, hookNo }, provided }) => ({
          plugin: plugin.name,
          pluginHookNo: hookNo,
          ...(provided.length > 0 ? { provided } : {}),
        })),
      },
      components,
    );
    for (const componentKey in components) {
      this.#componentValues.getOrSetDefault(componentKey).emit(id as UID, (components as any)[componentKey]);
    }
    const missingExpectedComponents: World.AnyComponentClass[] = [];
    for (const component of entityClass.componentsProvidedByPlugins ?? []) {
      if (!(component.key in components)) {
        missingExpectedComponents.push(component);
      }
    }
    if (missingExpectedComponents.length > 0) {
      throw new DevError(
        `Entity (${entityClass.key}) missing expected components after plugins were run: ${missingExpectedComponents.map((c) => c.key).join(", ")}`,
        { components, previousHooks: formatAppliedHooksStr() },
      );
    }
    return id as unknown as UID<T>;
  }

  public removeEntity(uid: UID): void {
    const entity = this.#entities.get(uid);
    if (!entity) return;

    // Remove the entity itself
    this.#entityLifetimePools.get(uid)?.dispose();
    this.#entityLifetimePools.delete(uid);
    this.#entities.delete(uid);

    // Remove all component values for this entity
    for (const key of Object.keys(entity)) {
      if (key.startsWith("_")) continue; // Skip internal properties like _id and _name
      const componentPubSub = this.#componentValues.get(key);
      if (componentPubSub) {
        // Emit undefined to notify subscribers before deletion
        componentPubSub.emit(uid, undefined as any);
        componentPubSub.delete(uid);
        entity[key] = undefined as any;
      }
    }
  }

  public getComponentAtom<T extends World.AnyComponentClass>(component: T): (uid: UID) => Atom<T["Type"] | undefined>;
  public getComponentAtom<T extends World.AnyComponentClass>(uid: UID, component: T): Atom<T["Type"] | undefined>;
  /** @internal */
  getComponentAtom<T extends World.AnyComponentClass>(
    uid: UID | T,
    component?: T,
  ): Atom<T["Type"] | undefined> | ((uid: UID) => Atom<T["Type"] | undefined>) {
    if (component == null || arguments.length === 1) {
      const component = uid as T;
      return (uid: UID) => this.getComponentAtom(uid, component);
    }
    invariant(typeof uid === "string", "UID must be a string", { uid });
    this.#onlyUnique(component, `getComponentAtom call`);
    return this.#componentValues.getOrSetDefault(component.key).getAtom(uid);
  }
  public getUniqueOrThrow<T extends World.AnyUniqueClass>(unique: T): T["Type"] {
    this.#onlyUnique(unique, `getUniqueOrThrow call`);
    const found = this.store.get(this.#uniques.getAtom(unique.key));
    invariant(found, "Unique not found", unique.key);
    return found as T["Type"];
  }
  public getComponentMaybe<T extends World.AnyComponentClass>(uid: UID, component: T): T["Type"] | undefined {
    this.#onlyUnique(component, `getComponentMaybe call`);
    return this.store.get(this.#componentValues.getOrSetDefault(component.key).getAtom(uid));
  }
}
